// TenantLoginPage - Animated split-screen sign-in gate for BLS tenant domains.
// Left: dark navy panel with orbiting real estate icons and ripple effect.
// Right: clean white panel with animated sign-in form.
// No OAuth - logins are limited to accounts created for prospects.
import { useState } from "react";
import _ from "lodash";
import {
  Home,
  DollarSign,
  Users,
  Search,
  Building2,
  Handshake,
  BarChart3,
  MapPin,
  TrendingUp,
} from "lucide-react";
import {
  Ripple,
  TechOrbitDisplay,
  AnimatedForm,
} from "../ui/modern-animated-sign-in";
import { userLogin, verifyEmail } from "../../services/v2/signupService";
import { demoRegister } from "../../services/v2/demoService";
import { updateUserDetailsToStorage } from "../../helpers/authHelpers";
import { useAppConfig } from "../../appConfig";
import { getLocalStorage, setLocalStorage } from "../../helpers/common";
import { emailValidation } from "../../helpers/validations";

const REMEMBER_KEY = "tenant_remember_email";

// Real estate themed orbiting icons. White + light-blue stay constant across
// every tenant - the icons themselves are not tenant-themed. Only the
// surrounding gradient and center anchor glow read from tenant themeVars.
const iconsArray = [
  {
    component: () => <Home size={20} color="#ffffff" />,
    className: "size-[36px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    duration: 22,
    delay: 0,
    radius: 100,
    path: false,
    reverse: false,
  },
  {
    component: () => <DollarSign size={20} color="#93c5fd" />,
    className: "size-[36px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    duration: 22,
    delay: 11,
    radius: 100,
    path: false,
    reverse: false,
  },
  {
    component: () => <Search size={28} color="#ffffff" />,
    className: "size-[48px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    radius: 190,
    duration: 26,
    delay: 0,
    path: false,
    reverse: false,
  },
  {
    component: () => <Users size={28} color="#93c5fd" />,
    className: "size-[48px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    radius: 190,
    duration: 26,
    delay: 13,
    path: false,
    reverse: false,
  },
  {
    component: () => <Building2 size={22} color="#ffffff" />,
    className: "size-[38px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    duration: 20,
    delay: 0,
    radius: 145,
    path: false,
    reverse: true,
  },
  {
    component: () => <Handshake size={22} color="#93c5fd" />,
    className: "size-[38px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    duration: 20,
    delay: 10,
    radius: 145,
    path: false,
    reverse: true,
  },
  {
    component: () => <BarChart3 size={30} color="#ffffff" />,
    className: "size-[52px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    radius: 250,
    duration: 28,
    delay: 0,
    path: false,
    reverse: true,
  },
  {
    component: () => <MapPin size={30} color="#93c5fd" />,
    className: "size-[52px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    radius: 250,
    duration: 28,
    delay: 14,
    path: false,
    reverse: true,
  },
  {
    component: () => <TrendingUp size={26} color="#ffffff" />,
    className: "size-[44px] border-none bg-white/15 shadow-lg backdrop-blur-sm",
    radius: 300,
    duration: 30,
    delay: 8,
    path: false,
    reverse: false,
  },
];

const SPLASH_GRADIENT_DEFAULT =
  "linear-gradient(135deg, #060d1a 0%, #0a1628 50%, #0f1f3a 100%)";
const ACCENT_DEFAULT = "#93c5fd";

// Convert "#rrggbb" to "rgba(r,g,b,a)" so the center anchor glow can derive
// from the same accent color the orbiting icons use.
const hexToRgba = (hex, alpha) => {
  const n = parseInt((hex || "").replace("#", ""), 16);
  if (Number.isNaN(n)) return `rgba(147, 197, 253, ${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const TenantLoginPage = ({ onLoginSuccess }) => {
  const config = useAppConfig();
  const tenant = config?.tenant;
  const logoSrc = tenant?.assets?.logos?.logoWhite || tenant?.assets?.logos?.logoV1;
  const isDemoMode = tenant?.demoMode;
  const demoEmail = tenant?.demoAccounts?.default?.email;
  const samlConfig = tenant?.auth?.saml;
  const isSamlEnabled = samlConfig?.enabled === true;

  // Tenant-themed splash backdrop. Defaults preserve the navy look for
  // Cove/MIBOR/BLS - these tenants don't define --splash-gradient or
  // --tenant-accent-color so they fall through. LAR overrides both in its
  // tenant JSON. The orbiting icons stay blue across every tenant.
  const accentColor =
    tenant?.themeVars?.["--tenant-accent-color"] || ACCENT_DEFAULT;
  const splashGradient =
    tenant?.themeVars?.["--splash-gradient"] || SPLASH_GRADIENT_DEFAULT;

  const rememberedEmail = getLocalStorage(REMEMBER_KEY);

  const [formData, setFormData] = useState(
    isDemoMode
      ? { firstName: "", lastName: "", email: "", organization: "" }
      : { email: rememberedEmail || (isDemoMode ? demoEmail : "") || "", pwd: "" }
  );
  const [loginError, setLoginError] = useState(null);
  const [rememberMe, setRememberMe] = useState(!!rememberedEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Demo mode - register prospect via clone-on-register
  const handleDemoSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoginError(null);

    const { firstName, lastName, email } = formData;
    if (!firstName?.trim() || !lastName?.trim() || !email?.trim() || !formData.organization?.trim()) {
      setLoginError("Please fill in all fields");
      setIsSubmitting(false);
      return;
    }
    if (!emailValidation(email.trim())) {
      setLoginError("Invalid email address");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await demoRegister({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        organization: formData.organization?.trim() || null,
        tenantSlug: tenant?.id || null,
        demoDataVersion: tenant?.demoDataVersion || 0,
      });

      if (_.get(response, "errorCode") === 0) {
        // Store prospect info for Clarity tagging
        const prospect = _.get(response, "result.prospect");
        if (prospect) {
          setLocalStorage("demoProspect", prospect);
        }
        // Store cloned demoTour IDs/slugs so the tour uses the prospect's data
        const demoTour = _.get(response, "result.demoTour");
        if (demoTour) {
          setLocalStorage("demoTourOverride", demoTour);
        }
        updateUserDetailsToStorage(response);
        localStorage.setItem("_loginTs", String(Date.now()));
        onLoginSuccess?.();
      } else {
        setLoginError(_.get(response, "message", "Registration failed"));
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        setLoginError("Demo is at capacity. Please try again later.");
      } else {
        setLoginError(err?.response?.data?.message || "Registration failed");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Standard login - email + password
  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoginError(null);

    const email = formData.email.trim();

    if (!emailValidation(email)) {
      setLoginError("Invalid email address");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await verifyEmail({ Email: email }).then((r) => r?.result);
      const userExist = _.get(res, "UserExists", false);
      const userVerified = _.get(res, "IsVerified", false);
      const isDeleted = _.get(res, "IsDeleted", false);

      if (!userExist || !userVerified || isDeleted) {
        setLoginError("Account not found or not verified");
        setIsSubmitting(false);
        return;
      }
    } catch {
      setLoginError("Unable to verify account");
      setIsSubmitting(false);
      return;
    }

    if (rememberMe) {
      setLocalStorage(REMEMBER_KEY, email);
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }

    try {
      const response = await userLogin({
        email,
        password: formData.pwd,
      });

      if (_.get(response, "errorCode") === 0) {
        updateUserDetailsToStorage(response);
        localStorage.setItem("_loginTs", String(Date.now()));
        onLoginSuccess?.();
      } else {
        setLoginError(_.get(response, "message", "Invalid credentials"));
      }
    } catch (err) {
      setLoginError(err?.response?.data?.message || "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = isDemoMode ? handleDemoSubmit : handleLoginSubmit;

  const formFields = isDemoMode
    ? {
        header: "Experience The Buyer Listing Service\u00AE",
        subHeader: "Enter your info for exclusive access.",
        fields: [
          {
            label: "First Name",
            name: "firstName",
            required: true,
            type: "text",
            placeholder: "Jane",
            value: formData.firstName,
            autoFocus: true,
            onChange: (e) => {
              setLoginError(null);
              setFormData((prev) => ({ ...prev, firstName: e.target.value }));
            },
          },
          {
            label: "Last Name",
            name: "lastName",
            required: true,
            type: "text",
            placeholder: "Smith",
            value: formData.lastName,
            onChange: (e) => {
              setLoginError(null);
              setFormData((prev) => ({ ...prev, lastName: e.target.value }));
            },
          },
          {
            label: "Email",
            name: "email",
            required: true,
            type: "email",
            placeholder: "you@yourmls.com",
            value: formData.email,
            onChange: (e) => {
              setLoginError(null);
              setFormData((prev) => ({ ...prev, email: e.target.value }));
            },
          },
          {
            label: "Organization",
            name: "organization",
            required: true,
            type: "text",
            placeholder: "Cove MLS",
            value: formData.organization,
            onChange: (e) => {
              setFormData((prev) => ({ ...prev, organization: e.target.value }));
            },
          },
        ],
        submitButton: "Get Access",
      }
    : {
        header: "Welcome back",
        subHeader: "Sign in to access real-time buyer demand data for your market.",
        fields: [
          {
            label: "Email",
            name: "email",
            required: true,
            type: "email",
            placeholder: "yourname@email.com",
            value: formData.email,
            autoFocus: !rememberedEmail,
            onChange: (e) => {
              setLoginError(null);
              setFormData((prev) => ({ ...prev, email: e.target.value }));
            },
          },
          {
            label: "Password",
            name: "pwd",
            required: true,
            type: "password",
            placeholder: "Enter your password",
            value: formData.pwd,
            autoFocus: !!rememberedEmail,
            onChange: (e) => {
              setLoginError(null);
              setFormData((prev) => ({ ...prev, pwd: e.target.value }));
            },
          },
        ],
        submitButton: "Sign In",
        textVariantButton: "Forgot your password?",
      };

  return (
    <section className="flex max-lg:justify-center" style={{ position: "fixed", inset: 0, zIndex: 9999, minHeight: "100vh" }}>
      {/* Left Side - Dark navy with orbiting icons */}
      <span
        className="flex flex-col justify-center w-1/2 max-lg:hidden relative overflow-hidden"
        style={{ background: splashGradient }}
      >
        {logoSrc && (
          <img
            src={logoSrc}
            alt="Logo"
            style={{
              position: "absolute",
              top: 32,
              left: 40,
              height: 28,
              width: "auto",
              zIndex: 10,
              filter: "brightness(1.1)",
            }}
          />
        )}
        <Ripple mainCircleSize={80} mainCircleOpacity={0.35} numCircles={8} darkMode />
        <TechOrbitDisplay iconsArray={iconsArray} />
        {/* Center anchor - subtle glowing dot */}
        <span style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: hexToRgba(accentColor, 0.6),
          boxShadow: `0 0 20px 8px ${hexToRgba(accentColor, 0.2)}`,
          zIndex: 5,
        }} />
      </span>

      {/* Right Side - Clean white with form */}
      <span className="w-1/2 h-[100dvh] flex flex-col justify-center items-center max-lg:w-full max-lg:px-[10%]" style={{ background: "#ffffff" }}>
        {/* Mobile logo */}
        {logoSrc && (
          <img
            src={logoSrc}
            alt="Logo"
            className="lg:hidden mb-8"
            style={{ height: 28, width: "auto", filter: "invert(1)" }}
          />
        )}
        {/* SSO button - shown as primary when SAML is enabled */}
        {isSamlEnabled && (
          <>
            <a
              href={`${(process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "")}${samlConfig.loginUrl}`}
              style={{
                display: "block",
                width: "100%",
                maxWidth: 360,
                padding: "14px 24px",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
                color: "#ffffff",
                fontFamily: "'Inter', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                textAlign: "center",
                textDecoration: "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.3)",
                marginBottom: 24,
              }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = "0 4px 16px rgba(37, 99, 235, 0.4)";
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = "0 2px 8px rgba(37, 99, 235, 0.3)";
                e.target.style.transform = "translateY(0)";
              }}
            >
              {samlConfig.buttonLabel || "Sign in with SSO"}
            </a>
            <div style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              maxWidth: 360,
              marginBottom: 24,
            }}>
              <span style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
              <span style={{
                padding: "0 16px",
                color: "#9ca3af",
                fontSize: 13,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
              }}>or</span>
              <span style={{ flex: 1, height: 1, background: "#e5e7eb" }} />
            </div>
          </>
        )}
        <AnimatedForm
          {...formFields}
          fieldPerRow={1}
          onSubmit={handleSubmit}
          goTo={isDemoMode ? undefined : () => {
            window.location.href = "/forgot-password";
          }}
          errorField={loginError}
          rememberMe={isDemoMode ? undefined : rememberMe}
          onRememberMeChange={isDemoMode ? undefined : setRememberMe}
          isSubmitting={isSubmitting}
        />
      </span>
    </section>
  );
};

export default TenantLoginPage;
